import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AddFilesToAlbumAction,
  AddNewAlbumAction,
  CloudState,
  DeleteItemsFromAlbumAction,
  LoadItemsAction,
  SetAlbumCoverUrlAction,
  SetAlbumIdAction,
  SetAlbumsAction,
  SetAttachParamsAction,
  SetItemsAction,
  SetSavedContentActionsAction,
  SetSelectedItemsAction,
  UploadCoverAction,
} from '$store/cloud/interface';
import { RootState } from '$store/rootReducer';
import { CloudMediaTabs } from '$store/models';

const initialState: CloudState = {
  albums: [],
  selectedAlbumId: '',
  itemsInfo: {},
  selectedItems: [],
  nextFrom: 0,
  isAlbumsLoading: true,
  isItemsLoading: true,
  isItemsLoadingMore: false,
  pickerId: '',
  routeName: '',
  routeParams: {},
  savedContentActions: [],
  filter: CloudMediaTabs.ALL,
};

export const { actions, reducer } = createSlice({
  name: 'cloud',
  initialState,
  reducers: {
    loadAlbums(state) {
      state.isAlbumsLoading = true;
    },
    setAlbums(state, action: SetAlbumsAction) {
      state.isAlbumsLoading = false;
      state.albums = action.payload;
    },
    addNewAlbum(state, action: AddNewAlbumAction) {
      state.albums = [action.payload, ...state.albums];
    },
    removeItemFromUI(state, action: PayloadAction<number>) {
      state.albums = state.albums.filter((album) => album.id !== action.payload);
    },
    loadItems(state, action: LoadItemsAction) {
      const { filter, albumId, isAsc } = action.payload;

      if (action.payload.isMore) {
        state.isItemsLoadingMore = true;
      } else {
        state.isItemsLoading = true;
      }

      state.filter = filter;
    },
    setItems(state, action: SetItemsAction) {
      const { albumId, items, isAsc, title, coverUrl, nextFrom, isMore } = action.payload;
      const itemsInfo = state.itemsInfo[albumId];

      state.itemsInfo[albumId] = {
        ...itemsInfo,
        items: isMore ? [...(itemsInfo.items || []), ...items] : items,
        isAsc,
        title,
        coverUrl,
        isLoading: false,
      };
      state.nextFrom = nextFrom;
      state.isItemsLoading = false;
      state.isItemsLoadingMore = false;
    },
    setAlbumCoverUrl(state, action: SetAlbumCoverUrlAction) {
      const { albumId, coverUrl } = action.payload;
      const itemsInfo = state.itemsInfo[albumId];

      state.itemsInfo[albumId] = {
        ...itemsInfo,
        coverUrl,
      };
    },
    setAlbumId(state, action: SetAlbumIdAction) {
      const albumId = action.payload;

      if (albumId !== state.selectedAlbumId) {
        state.itemsInfo[state.selectedAlbumId] = {
          ...state.itemsInfo[state.selectedAlbumId],
          isLoading: true,
        };
      }

      state.selectedAlbumId = albumId;
    },
    deleteItemsFromAlbum(_, action: DeleteItemsFromAlbumAction) {},
    removeItemsFromAlbum(state, action: DeleteItemsFromAlbumAction) {
      const { items } = action.payload;

      items.forEach(({ id, albumId }) => {
        const album = state.itemsInfo[albumId];
        if (album?.items) {
          album.items = album.items.filter((item) => item.id !== id);
        }

        const all = state.itemsInfo[-1];
        if (all?.items) {
          all.items = all.items.filter((item) => item.id !== id);
        }
      });
    },
    setSelectedItems(state, action: SetSelectedItemsAction) {
      state.selectedItems = action.payload;
    },
    setAttachParams(state, action: SetAttachParamsAction) {
      const { pickerId, routeParams, routeName } = action.payload;

      state.routeName = routeName;
      state.pickerId = pickerId;
      state.routeParams = routeParams;
    },
    reset(state) {
      state.selectedItems = [];
      state.savedContentActions = [];
      state.routeName = '';
      state.pickerId = '';
      state.routeParams = {};
    },
    setSavedContentActions(state, action: SetSavedContentActionsAction) {
      state.savedContentActions = [...state.savedContentActions, ...action.payload];
    },
    addFilesToAlbum(state, action: AddFilesToAlbumAction) {
      state.isItemsLoading = true;
    },
    uploadCover(_, action: UploadCoverAction) {},
  },
});

export { reducer as cloudReducer, actions as cloudActions };
export const cloudSelector = (state: RootState) => state.cloud;
